import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import {
  CashbackFilter,
  CoinTransactionParams,
  CustomerScopeType,
  DateRangeParams,
  FlaggedOrdersParams,
  FoodItemParams,
  PaginationParams,
  PilotsParams,
} from '../types';
import {
  brandApi,
  categoriesApi,
  couponsApi,
  loyaltyApi,
  mealsApi,
  pilotsApi,
  ordersApi,
  kitchensApi,
  externalClientApi,
  getAllDeliveryRates,
  usersApi,
} from '../api';

export const useFetch = () => {
  /// foooood
  const useFetchFoodItem = <T>(
    id?: string,
    params?: FoodItemParams,
    options?: any
  ) =>
    useQuery<T>(
      ['food-items', id, params],
      () => mealsApi.getFoodItems(id, params) as T,
      options
    );

  const useFetchFoodGroup = <T>(
    id?: string,
    params?: FoodItemParams,
    options?: any
  ) =>
    useQuery<T>(
      ['food-group', id, params],
      () => mealsApi.getFoodGroups(id, params) as T,
      options
    );

  const useFetchOptions = (params?: FoodItemParams, options?: any) =>
    useQuery(['options', params], () => mealsApi.getOptions(params), options);

  /// brand
  const useFetchBrand = <T>(slug?: string, options?: any) =>
    useQuery<T>(['brands', slug], () => brandApi.getBrand(slug) as T, options);

  /// category
  const useFetchCategory = <T>(slug?: string, options?: any) =>
    useQuery<T>(
      ['category', slug],
      () => categoriesApi.getCategory(slug) as T,
      options
    );

  const useFetchAllPilots = (
    slug?: string,
    params?: PilotsParams,
    options?: any,
    useAdmin?: boolean
  ) =>
    useQuery(
      ['pilots', params, slug],
      () => pilotsApi.getAllPilots(slug, params, useAdmin),
      options
    );

  const useSearchPilots = (
    searchTerm: string,
    options?: {
      enabled?: boolean;
      debounceMs?: number;
      kitchenSlug?: string;
      isPublic?: boolean;
    }
  ) => {
    const debounceMs = options?.debounceMs ?? 1000;
    const [debouncedSearch] = useDebounce(searchTerm, debounceMs);

    const query = useQuery(
      ['pilots-search', options?.kitchenSlug, options?.isPublic, debouncedSearch],
      () =>
        pilotsApi.getAllPilots(options?.kitchenSlug ?? '', {
          search: debouncedSearch,
          ...(options?.isPublic && { isPublic: true }),
        }),
      {
        enabled: options?.enabled !== false && debouncedSearch.length > 0,
        keepPreviousData: true,
      }
    );

    return {
      ...query,
      isDebouncing: searchTerm !== debouncedSearch,
    };
  };

  const useFetchPilotSummary = (options?: any) =>
    useQuery(['pilots-summary'], () => pilotsApi.getPilotsSummary(), options);

  // coupons
  const useFetchCashback = (options?: any) =>
    useQuery(['cashback'], () => couponsApi.getActiveCashback(), options);

  const useFetchCashbackTransactions = (
    params?: CashbackFilter,
    options?: any
  ) =>
    useQuery(
      ['cashback-transactions'],
      () => couponsApi.getCashbackTransactions(params),
      options
    );

  const useFetchLoyaltyDashboard = (params?: DateRangeParams, options?: any) =>
    useQuery(
      ['loyalty-dashboard', params],
      () => loyaltyApi.getLoyaltyDashboard(params),
      options
    );

  const useFetchCoinTransactions = (
    params?: CoinTransactionParams,
    options?: any
  ) =>
    useQuery(
      ['coin-transactions', params],
      () => loyaltyApi.listCoinTransaction(params),
      options
    );

  const useFetchLoyaltyTiers = (params?: PaginationParams, options?: any) =>
    useQuery(
      ['loyalty-tiers', params],
      () => loyaltyApi.getAllTier(params),
      options
    );

  const useFetchAllActions = (params?: PaginationParams, options?: any) =>
    useQuery(
      ['loyalty-actions', params],
      () => loyaltyApi.getAllActions(params),
      options
    );

  const useFetchBudgets = (options?: any) =>
    useQuery(['budgets'], () => couponsApi.getAllBudgets(), options);

  const useFetchFlaggedOrders = (params?: FlaggedOrdersParams, options?: any) =>
    useQuery(
      ['flagged-orders', params],
      () => ordersApi.getFlaggedOrders(params),
      options
    );

  const useFetchKitchenRequests = (params?: PaginationParams, options?: any) =>
    useQuery(
      ['kitchen-requests', params],
      () => kitchensApi.getKitchenRequests(params),
      options
    );

  // Delivery rates
  const useFetchDeliveryRates = (options?: any) =>
    useQuery(['delivery-rates'], () => getAllDeliveryRates(), options);

  const useFetchKitchenDeliveryConfig = (
    params?: { isShadow?: string; externalClientId?: number },
    options?: any
  ) =>
    useQuery(
      ['kitchen-delivery-config', params],
      () => kitchensApi.getAllKitchenDeliveryConfig(params),
      options
    );

  const useFetchExternalClientNames = (options?: any) =>
    useQuery(
      ['external-client-names'],
      () => externalClientApi.getNames(),
      options
    );

  const useFetchCustomerSegment = (
    scope: CustomerScopeType,
    id: number,
    options?: any
  ) => {
    return useQuery(
      ['fetch-customer-segment'],
      async () => {
        const response = await usersApi.getCustomerSegment(scope, id);
        return response.data.data; // Gets the actual customer segment data
      },
      options
    );
  };
  // wallet
  const useFetchUserWallet = (email: string, options?: any) =>
    useQuery(
      ['user-wallet', email],
      () => usersApi.getUserWallet(email),
      options
    );

  return {
    useFetchBrand,
    useFetchOptions,
    useFetchBudgets,
    useFetchFoodItem,
    useFetchCategory,
    useFetchCashback,
    useFetchFoodGroup,
    useFetchAllPilots,
    useSearchPilots,
    useFetchAllActions,
    useFetchUserWallet,
    useFetchPilotSummary,
    useFetchLoyaltyTiers,
    useFetchDeliveryRates,
    useFetchFlaggedOrders,
    useFetchKitchenRequests,
    useFetchCoinTransactions,
    useFetchLoyaltyDashboard,
    useFetchCashbackTransactions,
    useFetchKitchenDeliveryConfig,
    useFetchExternalClientNames,
    useFetchCustomerSegment,
  };
};
